/**
 * Opções do dropdown de Demandante, no cadastro da Realização Destaque.
 *
 * O demandante vem da tabela de UORs (`uors__c`). Esta função transforma as UORs lidas em
 * opções do `CustomSelect` e fica fora do componente para poder ser exercitada sem montar a
 * tela inteira.
 */

/**
 * A forma da opção é o contrato com `handleDemandanteChange`: `value` vira
 * `nec.nm_uor_sltt` e `cd_uor_sltt` vira `nec.cd_uor_sltt` na realização gravada.
 */
export type DemandanteOption = {
  label: string;
  value: string;
  cd_uor_sltt: string;
};

/** Valor do campo Demandante: a opção escolhida, com código, ou só o nome em rascunho e edição gravados sem ele. */
type DemandanteEscolhido = {
  value?: unknown;
  cd_uor_sltt?: unknown;
};

type UorRecebida = {
  cd_uor?: unknown;
  tx_nm_uor_cmt?: unknown;
  tx_nm_uor_rdz?: unknown;
};

type UorValida = {
  codigo: string;
  nome: string;
};

const porNome = new Intl.Collator('pt-BR', { sensitivity: 'base' });
const porCodigo = new Intl.Collator('pt-BR', { numeric: true });

function texto(valor: unknown): string {
  return valor === null || valor === undefined ? '' : String(valor).trim();
}

/**
 * Diz se o demandante está preenchido. A tela guarda texto quando a NEC traz o demandante e a
 * opção escolhida quando a realização não tem NEC. Lista, texto vazio e opção sem valor contam como vazio.
 */
export function isDemandanteFilled(valor: unknown): boolean {
  if (typeof valor === 'string') return texto(valor).length > 0;
  if (!valor || typeof valor !== 'object') return false;

  return texto((valor as { value?: unknown }).value).length > 0;
}

/**
 * Nome que a UOR mostra e grava: o nome reduzido (`tx_nm_uor_rdz`) quando a UOR o tem, e, enquanto
 * o BB não o envia, o nome completo (`tx_nm_uor_cmt`).
 */
function nomeDaUor(uor: UorRecebida | null | undefined): string {
  return texto(uor?.tx_nm_uor_rdz) || texto(uor?.tx_nm_uor_cmt);
}

/** Uma UOR por código, só com código e nome preenchidos. A primeira ocorrência do código vale. */
function uorsValidas(uors: unknown): UorValida[] {
  if (!Array.isArray(uors)) return [];

  const porCodigoUnico = new Map<string, UorValida>();

  uors.forEach((uor: UorRecebida | null | undefined) => {
    const codigo = texto(uor?.cd_uor);
    const nome = nomeDaUor(uor);

    if (!codigo || !nome || porCodigoUnico.has(codigo)) return;

    porCodigoUnico.set(codigo, { codigo, nome });
  });

  return Array.from(porCodigoUnico.values());
}

function contarPorNome(uors: UorValida[]): Map<string, number> {
  const contagem = new Map<string, number>();

  uors.forEach(({ nome }) => contagem.set(nome, (contagem.get(nome) ?? 0) + 1));

  return contagem;
}

/**
 * Monta as opções em ordem alfabética de nome. UORs com o mesmo nome e códigos diferentes
 * recebem o código no rótulo, porque sem ele o cadastrante não tem como distingui-las.
 */
export function buildDemandanteOptions(uors: unknown): DemandanteOption[] {
  const validas = uorsValidas(uors);
  const ocorrenciasDoNome = contarPorNome(validas);

  return validas
    .sort((a, b) => porNome.compare(a.nome, b.nome) || porCodigo.compare(a.codigo, b.codigo))
    .map(({ codigo, nome }) => ({
      label: (ocorrenciasDoNome.get(nome) ?? 0) > 1 ? `${nome} (${codigo})` : nome,
      value: nome,
      cd_uor_sltt: codigo,
    }));
}

/**
 * Diz se a opção é a escolhida no campo Demandante.
 *
 * O `react-select` compara pelo `value`, que aqui é o nome: com duas UORs de mesmo nome ele
 * marcava as duas. Quando o valor escolhido traz o código, a comparação é pelo código. Rascunho
 * e edição gravados antes do código ser congelado só têm o nome, e continuam comparados por ele.
 */
export function isDemandanteOptionSelected(option: DemandanteOption, selected: readonly DemandanteEscolhido[]): boolean {
  return selected.some(escolhido => {
    const codigo = texto(escolhido?.cd_uor_sltt);

    return codigo ? codigo === option.cd_uor_sltt : escolhido?.value === option.value;
  });
}
